import type { ProductProfile } from '@fotoboek/domain';

export type WizardLocation = 'local' | 'cloud';

export type WizardDraft = {
  productProfileId: string;
  title: string;
  description: string;
  pageCount: number;
  templateId: string | null;
  location: WizardLocation;
};

export function createInitialWizardDraft(defaultProfile: ProductProfile): WizardDraft {
  return {
    productProfileId: defaultProfile.id,
    title: '',
    description: '',
    pageCount: defaultProfile.pageCount.default,
    templateId: null,
    location: 'local',
  };
}

/** Snaps an arbitrary page count to the nearest valid value for a profile. */
export function clampPageCountToProfile(pageCount: number, profile: ProductProfile): number {
  const { min, max, step } = profile.pageCount;
  const clamped = Math.min(max, Math.max(min, pageCount));
  const remainder = (clamped - min) % step;
  if (remainder === 0) return clamped;
  // Round to the nearer valid step rather than always flooring.
  const lower = clamped - remainder;
  const upper = Math.min(max, lower + step);
  return pageCount - lower <= upper - pageCount ? lower : upper;
}

export function validateTitle(title: string): string | null {
  return title.trim().length === 0 ? 'wizard.details.titleRequired' : null;
}

export function canUseCloudLocation(
  authStatus: 'unknown' | 'guest' | 'authenticated' | 'offline',
): boolean {
  return authStatus === 'authenticated';
}

export const WIZARD_STEP_KEYS = ['product', 'details', 'template', 'photos', 'location'] as const;
export type WizardStepKey = (typeof WIZARD_STEP_KEYS)[number];
